import { useDeepBoxAdminStore } from '@/stores/deepbox/admin/admin.ts'
import { useApi } from '@/composables/api.ts'
import { useBoxHiddenNodes } from '@/composables/box/box-hidden-nodes.ts'
import type { SearchData } from '@deepcloud/api-deep-box'

interface SearchParamsPayload {
  nodeId: string
  query?: string
  offset?: number
  limit?: number
}

export const useDeepBoxSearchStore = defineStore('deepBoxSearch', () => {
  const fetchSearchResultsPending = ref(false)
  const fetchNextSearchResultsPending = ref(false)
  const isSearchNavigationPending = ref(false)
  const searchResults = ref<SearchData | undefined>(undefined)
  const lastSearchParamsPayload = ref<SearchParamsPayload | undefined>(
    undefined,
  )

  const { deepBoxAPI } = useApi()
  const deepBoxAdminStore = useDeepBoxAdminStore()
  const { canShowHiddenNodes } = useBoxHiddenNodes(
    computed(() => deepBoxAdminStore.adminBox),
  )

  const abortController = ref<AbortController>()

  function abortOrCreateNewAbortController() {
    if (abortController.value) {
      abortController.value.abort()
    }
    abortController.value = new AbortController()
  }

  function clearAbortController() {
    abortController.value = undefined
  }

  function buildSearchQueryParams(payload: SearchParamsPayload) {
    return {
      parentNodeId: payload.nodeId,
      q: payload.query,
      offset: payload.offset,
      limit: payload.limit,
      ...(canShowHiddenNodes.value ? { includeHidden: true } : {}),
    }
  }

  async function fetchSearchResults(payload: {
    nodeId: string
    query?: string
    offset?: number
    limit?: number
  }) {
    abortOrCreateNewAbortController()

    fetchSearchResultsPending.value = true
    try {
      lastSearchParamsPayload.value = { ...payload }
      const res = await deepBoxAPI.value.search.get(
        buildSearchQueryParams(payload),
        { signal: abortController.value?.signal },
      )
      searchResults.value = { ...res.data }
      clearAbortController()
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    } finally {
      fetchSearchResultsPending.value = false
    }
  }

  async function fetchNextSearchResults(payload: {
    nodeId: string
    query?: string
    offset?: number
    limit?: number
  }) {
    abortOrCreateNewAbortController()

    fetchNextSearchResultsPending.value = true
    try {
      lastSearchParamsPayload.value = { ...payload }
      const res = await deepBoxAPI.value.search.get(
        buildSearchQueryParams(payload),
        { signal: abortController.value?.signal },
      )
      const searchResultsItems = searchResults.value?.items || []
      searchResults.value = {
        ...res.data,
        items: [...searchResultsItems, ...res.data.items],
      }

      clearAbortController()
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    } finally {
      fetchNextSearchResultsPending.value = false
    }
  }

  async function refresh() {
    if (lastSearchParamsPayload.value) {
      await fetchSearchResults(lastSearchParamsPayload.value)
    }
  }

  return {
    // state
    lastSearchParamsPayload,
    fetchSearchResultsPending,
    fetchNextSearchResultsPending,
    isSearchNavigationPending,
    searchResults,
    // actions
    fetchSearchResults,
    fetchNextSearchResults,
    refresh,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useDeepBoxSearchStore, import.meta.hot),
  )
}
